import { gql, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { useGroupRules, UserGroupRules } from 'src/graphql/entities/group-rules';
import { generateFieldModifiersMap } from 'src/graphql/utils';
import { useNotifications } from 'src/utils/hooks';

type RestoreDeletedConversationResponse = {
  restoreDeletedConversation: {
    conversationId: string;
  };
};

const RESTORE_CONVERSATION_MUTATION = gql`
  mutation RestoreDeletedConversation($params: RestoreDeletedConversationInput!) {
    restoreDeletedConversation(params: $params) {
      conversationId
    }
  }
`;

export const useRestoreConversation = () => {
  const { showSuccessAlert, showErrorAlert } = useNotifications();
  const { groupRules } = useGroupRules();
  const [restoreConversationMutation] = useMutation<RestoreDeletedConversationResponse>(
    RESTORE_CONVERSATION_MUTATION,
    {
      update: (cache, result) => {
        if (!result.data?.restoreDeletedConversation?.conversationId) {
          return;
        }
        const id = cache.identify({
          __typename: 'Conversation',
          id: result.data.restoreDeletedConversation.conversationId,
        });
        cache.modify({
          id,
          fields: generateFieldModifiersMap({
            deletedAt: null,
            __typename: 'Conversation',
          }),
        });
      },
      onCompleted: () => {
        showSuccessAlert('Conversation was restored');
      },
      onError: () => {
        showErrorAlert(`Could not restore conversation`);
      },
    },
  );

  const restoreConversation = useCallback(
    async (conversationId: string) => {
      if (groupRules[UserGroupRules.AccessToRestoreDeletedConversations]) {
        await restoreConversationMutation({
          variables: {
            params: {
              conversationId,
            },
          },
        });
      }
    },
    [groupRules, restoreConversationMutation],
  );

  return {
    restoreConversation,
  };
};
